import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {useDeleteProduction} from '../../../hooks/productions/useProductions';
import {ReactComponent as CaretDown} from '../../../assets/images/caret-down.svg';
import ArrButton from '../../../components/common/arr-button/ArrButton';

const DeleteProductionApiTest: React.FC = () => {
  const [productionId, setProductionId] = useState<string>('dEs4TkZDQkNBKzQ9');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const deleteProductionMutation = useDeleteProduction(
    (data) => {
      console.log('Production deleted successfully:', data);
    },
    (error) => {
      console.error('Error deleting production:', error);
    }
  );

  const handleTestApi = () => {
    deleteProductionMutation.mutate(productionId);
  };

  const handleReset = () => {
    setProductionId('dEs4TkZDQkNBKzQ9');
  };

  const handleCopyJson = async () => {
    try {
      await navigator.clipboard.writeText(productionId);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyResponse = async () => {
    if (deleteProductionMutation.data) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(deleteProductionMutation.data, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <Box className="demo-page" padding={3}>
      <Typography
        variant="h4"
        className="typo-text headline2_size"
        gutterBottom
      >
        🧪 Delete Production API Test
      </Typography>
      <Typography
        className="typo-text body3_size"
        sx={{color: '#666', marginBottom: 2}}
      >
        Delete a production by its encrypted production ID
      </Typography>

      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          padding: 2,
          borderRadius: 1,
          marginBottom: 3,
        }}
      >
        <Typography variant="h6" className="typo-text subhead2_size">
          Request Parameters
        </Typography>

        <Box sx={{display: 'flex', gap: 2, marginTop: 2, flexWrap: 'wrap'}}>
          <TextField
            label="Production ID (Encrypted)"
            type="text"
            value={productionId}
            onChange={(e) => setProductionId(e.target.value)}
            size="small"
            placeholder="Enter encrypted production ID"
            sx={{minWidth: '300px'}}
            helperText="This is the encrypted production ID from the URL path parameter"
          />
        </Box>

        <Box sx={{marginTop: 2, display: 'flex', gap: 2}}>
          <Button
            variant="contained"
            color="error"
            onClick={handleTestApi}
            disabled={deleteProductionMutation.isPending}
          >
            {deleteProductionMutation.isPending
              ? '⏳ Deleting...'
              : '🗑️ Delete Production'}
          </Button>
          <Button variant="outlined" onClick={handleReset}>
            🔄 Reset
          </Button>
        </Box>
      </Box>

      {/* Loading State */}
      {deleteProductionMutation.isPending && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#e3f2fd',
            borderRadius: 1,
            border: '1px solid #2196f3',
          }}
        >
          <Typography className="typo-text body2_size">
            ⏳ Deleting production... Making API request...
          </Typography>
        </Box>
      )}

      {/* Error State */}
      {deleteProductionMutation.error && (
        <Box
          sx={{
            padding: 3,
            backgroundColor: '#ffebee',
            borderRadius: 1,
            border: '1px solid #f44336',
          }}
        >
          <Typography className="typo-text subhead3_size" color="error">
            ❌ Error
          </Typography>
          <Typography className="typo-text body3_size" color="error">
            {deleteProductionMutation.error.message}
          </Typography>
        </Box>
      )}

      {/* Success State - Display Response */}
      {deleteProductionMutation.data && !deleteProductionMutation.isPending && (
        <Box>
          <Typography
            variant="h6"
            className="typo-text subhead2_size"
            gutterBottom
          >
            ✅ API Response
          </Typography>

          {/* Success Message */}
          <Box
            sx={{
              padding: 2,
              backgroundColor: '#e8f5e9',
              borderRadius: 1,
              marginBottom: 2,
              border: '1px solid #4caf50',
            }}
          >
            <Typography className="typo-text label2_size" gutterBottom>
              🎉 Production Deleted Successfully!
            </Typography>
            <Typography className="typo-text body3_size">
              {deleteProductionMutation.data.message}
            </Typography>
          </Box>

          {/* Expandable Sections */}
          <Box sx={{marginTop: 2}}>
            {/* Request Details */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  📤 Request Details
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{marginBottom: 2}}>
                  <ArrButton
                    variant={copySuccess ? 'secondary' : 'primary'}
                    size="small"
                    onClick={handleCopyJson}
                  >
                    {copySuccess ? '✅ Copied!' : '📋 Copy Production ID'}
                  </ArrButton>
                </Box>
                <Typography className="typo-text body3_size" gutterBottom>
                  <strong>Production ID:</strong> {productionId}
                </Typography>
                <Typography className="typo-text body3_size">
                  <strong>Method:</strong> DELETE
                </Typography>
              </AccordionDetails>
            </Accordion>

            {/* Response Data */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  📥 Response Data
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{marginBottom: 2}}>
                  <ArrButton
                    variant={copySuccess ? 'secondary' : 'primary'}
                    size="small"
                    onClick={handleCopyResponse}
                  >
                    {copySuccess ? '✅ Copied!' : '📋 Copy Response'}
                  </ArrButton>
                </Box>
                <pre
                  style={{
                    fontSize: '12px',
                    overflow: 'auto',
                    maxHeight: '300px',
                  }}
                >
                  {JSON.stringify(deleteProductionMutation.data, null, 2)}
                </pre>
              </AccordionDetails>
            </Accordion>

            {/* Full Response */}
            <Accordion>
              <AccordionSummary expandIcon={<CaretDown />}>
                <Typography className="typo-text label2_size">
                  📄 Full JSON Response
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    padding: 2,
                    backgroundColor: '#263238',
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: '500px',
                  }}
                >
                  <pre
                    style={{
                      color: '#4caf50',
                      fontSize: '12px',
                      margin: 0,
                      fontFamily: 'monospace',
                    }}
                  >
                    {JSON.stringify(deleteProductionMutation.data, null, 2)}
                  </pre>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      )}

      {/* Initial State */}
      {!deleteProductionMutation.isPending &&
        !deleteProductionMutation.data &&
        !deleteProductionMutation.error && (
          <Box
            sx={{
              padding: 3,
              backgroundColor: '#fff3e0',
              borderRadius: 1,
              border: '1px solid #ff9800',
            }}
          >
            <Typography className="typo-text body2_size">
              👆 Click "Delete Production" button to make the API request
            </Typography>
          </Box>
        )}

      {/* Network Details */}
      <Box sx={{marginTop: 4, padding: 2, backgroundColor: '#fafafa'}}>
        <Typography variant="h6" className="typo-text subhead3_size">
          🌐 Network Details
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Endpoint:</strong> /productions/api/v1/production/delete/
          {productionId}
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Method:</strong> DELETE
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Path Parameter:</strong> {productionId}
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Base URL:</strong>{' '}
          https://arr-api-productions-dev.us2kd2w30.k8d.consiliotest.com/
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Expected Response:</strong>
        </Typography>
        <pre
          style={{
            fontSize: '12px',
            backgroundColor: '#e8f5e8',
            padding: '8px',
            borderRadius: '4px',
          }}
        >
          {`{
  "message": "Production deleted successfully"
}`}
        </pre>
      </Box>

      {/* Hook Usage Example */}
      <Box sx={{marginTop: 4, padding: 2, backgroundColor: '#f0f8ff'}}>
        <Typography variant="h6" className="typo-text subhead3_size">
          💻 Hook Usage Example
        </Typography>
        <pre
          style={{
            fontSize: '12px',
            backgroundColor: '#fff',
            padding: '12px',
            borderRadius: '4px',
            overflow: 'auto',
          }}
        >
          {`import { useDeleteProduction } from '../../../hooks/productions/useProductions';

const MyComponent = () => {
  const deleteProduction = useDeleteProduction(
    (data) => console.log('Success:', data),
    (error) => console.error('Error:', error)
  );

  const handleDelete = () => {
    deleteProduction.mutate('dEs4TkZDQkNBKzQ9');
  };

  return (
    <button 
      onClick={handleDelete}
      disabled={deleteProduction.isPending}
    >
      {deleteProduction.isPending ? 'Deleting...' : 'Delete Production'}
    </button>
  );
};`}
        </pre>
      </Box>

      {/* Warning Notice */}
      <Box
        sx={{
          marginTop: 4,
          padding: 2,
          backgroundColor: '#fff3e0',
          border: '1px solid #ff9800',
        }}
      >
        <Typography
          variant="h6"
          className="typo-text subhead3_size"
          color="warning"
        >
          ⚠️ Important Notice
        </Typography>
        <Typography className="typo-text body3_size">
          This operation permanently deletes the production. This action cannot
          be undone. Make sure you have the correct encrypted production ID
          before proceeding.
        </Typography>
      </Box>
    </Box>
  );
};

export default DeleteProductionApiTest;
